import type { BillingPaymentViewModel } from '@/components/billing/billing-types';
import { paymentStatusBadgeVariant } from '@/components/billing/payment-status';
import { ConditionField } from '@/components/tree-dnd/tree-dnd-conditions/condition-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LoaderButton } from '@/components/loader-button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

interface PaymentDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: BillingPaymentViewModel | null;
  hasStoredPaymentMethod: boolean;
  isPaying: boolean;
  onPayWithStoredCard: (paymentId: string) => void;
}

function ReadOnlyValue({ children }: { children: string }) {
  return <p className="text-sm">{children}</p>;
}

export function PaymentDetailDrawer({
  open,
  onOpenChange,
  payment,
  hasStoredPaymentMethod,
  isPaying,
  onPayWithStoredCard,
}: PaymentDetailDrawerProps) {
  const { LL } = useI18nContext();

  if (!payment) {
    return null;
  }

  const isPayable =
    payment.status === 'pending' || payment.status === 'past_due';
  const canPayWithStoredCard = isPayable && hasStoredPaymentMethod;
  const canPayViaLink = isPayable && payment.paymentUrl !== null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[min(100vw,26rem)] flex-col gap-0 p-0 sm:max-w-[26rem]"
      >
        <SheetHeader className="space-y-3 border-b px-6 py-4 text-left">
          <div className="space-y-1.5 pr-8">
            <SheetTitle>{LL.billingPage.invoiceDetails()}</SheetTitle>
            <SheetDescription>
              {payment.description === '-'
                ? payment.amount
                : `${payment.description} · ${payment.amount}`}
            </SheetDescription>
          </div>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">
          <div className="flex flex-col gap-6">
            <ConditionField label={LL.billingPage.invoiceDate()}>
              <ReadOnlyValue>{payment.date}</ReadOnlyValue>
            </ConditionField>
            <ConditionField label={LL.billingPage.paymentDescription()}>
              <ReadOnlyValue>{payment.description}</ReadOnlyValue>
            </ConditionField>
            <ConditionField label={LL.billingPage.invoiceAmount()}>
              <ReadOnlyValue>{payment.amount}</ReadOnlyValue>
            </ConditionField>
            <ConditionField label={LL.billingPage.invoiceStatus()}>
              <Badge
                variant={paymentStatusBadgeVariant(payment.status)}
                className={cn(
                  (payment.status === 'void' || payment.status === 'other') &&
                    'text-muted-foreground'
                )}
              >
                {payment.statusLabel}
              </Badge>
            </ConditionField>

            {canPayWithStoredCard ? (
              <LoaderButton
                type="button"
                onClick={() => onPayWithStoredCard(payment.id)}
                disabled={isPaying}
                isLoading={isPaying}
              >
                {LL.billingPage.payNowWithCard()}
              </LoaderButton>
            ) : null}

            {canPayViaLink ? (
              <Button
                asChild
                variant={canPayWithStoredCard ? 'outline' : 'default'}
              >
                <a href={payment.paymentUrl!} target="_blank" rel="noreferrer">
                  {LL.billingPage.changePaymentMethod()}
                </a>
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t px-6 py-4">
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {LL.billingPage.close()}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
